import type { TimeInput } from './types';
import { instantToWallTime } from './instant-to-wall-time';

/**
 * Do two instants fall on the same calendar day, as observed in `zone`?
 *
 * The zone-aware replacement for the `a.getFullYear() === b.getFullYear() && …`
 * shape, which reads the RUNTIME's zone and therefore decides a day boundary on
 * whichever clock the machine happens to be set to (BUG-1586). Any surface that
 * switches presentation on "is this today" — a today-vs-dated timestamp, a day
 * divider, a "since yesterday" band — owes that decision to an explicit zone,
 * exactly as {@link formatEventTime} owes it the rendering.
 *
 * Returns **null** rather than guessing when the question cannot be answered —
 * an invalid or empty `zone`, or an unparseable instant. A boolean fallback
 * would be a confident wrong answer, which is the failure this exists to
 * remove; callers render a placeholder instead (ADR 024 — Time Model's
 * refusal-to-guess rule).
 *
 * @param a - One instant.
 * @param b - The other instant. Order does not matter.
 * @param zone - IANA zone the calendar day is read in.
 * @returns `true`/`false` in that zone, or `null` when it cannot be determined.
 * @sideEffect none — pure.
 */
export function isSameDayInZone({
    a,
    b,
    zone,
}: {
    a: TimeInput;
    b: TimeInput;
    zone: string;
}): boolean | null {
    if (!zone) return null;
    const left = instantToWallTime({ instant: a, zone });
    const right = instantToWallTime({ instant: b, zone });
    if (!left || !right) return null;
    return left.year === right.year && left.month === right.month && left.day === right.day;
}
